import * as React from 'react';
import {
    DragSource,
    DropTarget,
    ConnectDragSource,
    ConnectDragPreview,
    ConnectDropTarget,
    // @ts-expect-error
} from 'react-dnd';
// @ts-expect-error
import {getEmptyImage} from 'react-dnd-html5-backend';

import classnames from 'classnames';

import {deserializeUrlParam} from 'nutshell-core/deserialize-url-param';
import {serialize} from 'nutshell-core/serialize';
import type {FilterObject, CoreEntityType} from 'nutshell-core/types';

import {Checkbox} from 'shells/checkbox/checkbox';
// @ts-expect-error - imported from .js/.jsx file
import {ArrowsAscIcon} from 'shells/icon/svg/arrows-asc-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ArrowsDescIcon} from 'shells/icon/svg/arrows-desc-icon';
// @ts-expect-error - imported from .js/.jsx file
import {FilterIcon} from 'shells/icon/svg/filter-icon';

import {dragItemTypes} from '../../dnd/drag-item-types';
import type {DndListColumnItem} from '../../dnd/drag-item-types';
import {PopoverForm} from '../../filtering/popover-form';
// @ts-expect-error
import getClassesMixin from '../../mixins/get-classes';

import styles from './column-header.module.css';

const columnSource = {
    beginDrag(props: OwnProps): DndListColumnItem {
        return {
            id: props.name,
            // @ts-expect-error
            index: props.index,
            title: props.field.title,
            type: props.entityType,
            width: props.width,
        };
    },

    // @ts-expect-error
    canDrag(props) {
        return !(props.name === 'name');
    },

    // @ts-expect-error
    endDrag(props, monitor) {
        if (!monitor.didDrop()) return;

        const item = monitor.getItem();
        const newIndex = monitor.getDropResult();

        props.columnWasMoved(item.id, newIndex.id);
    },
} as const;

const cardTarget = {
    drop(props: OwnProps) {
        return {id: props.name};
    },
} as const;

// @ts-expect-error
function collect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    };
}

// @ts-expect-error
function connectDrop(connect, monitor): TargetProps {
    const draggedItem = monitor.getItem();

    return {
        connectDropTarget: connect.dropTarget(),
        draggingIndex: draggedItem ? draggedItem.index : null,
        isOver: monitor.isOver(),
    };
}

type OwnProps = {
    name: string;
    index: number;
    entityType: CoreEntityType;
    field: any;
    filter: FilterObject | null | undefined;
    width: number;
    onSort: (...args: any) => any;
    isSorted: boolean;
    isDuplicatesMode: boolean;
    sortDirection: string | null | undefined;
    onSubmit: (arg1: FilterObject) => any | null | undefined;
    onUpdate: (arg1: FilterObject, arg2: FilterObject) => any | null | undefined;
    onRemove: (...args: any) => any | null | undefined;
    onPopoverClose: (...args: any) => any;
    onPopoverOpen: (...args: any) => any;
    onSelectAll: (...args: any) => any | null | undefined;
    allSelected: boolean;
    columnWasMoved: (...args: any) => any;
};

type SourceProps = {
    isDragging: boolean;
    connectDragSource: ConnectDragSource;
    connectDragPreview: ConnectDragPreview;
};

type TargetProps = {
    isOver: boolean;
    draggingIndex: number | null | undefined;
    connectDropTarget: ConnectDropTarget;
};

type Props = OwnProps & SourceProps & TargetProps;

type State = {
    popoverOpen: boolean;
    maxTitleWidth: number;
};

class ColumnHeaderComponent extends React.Component<Props, State> {
    columnWrapperRef: HTMLElement | null | undefined;
    selectAllDivRef: HTMLElement | null | undefined;
    filterRef: HTMLElement | null | undefined;
    sortRef: HTMLElement | null | undefined;

    getClasses = getClassesMixin;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            popoverOpen: false,
            maxTitleWidth: 100,
        };
    }

    componentDidMount() {
        this.updateMaxTitleWidth();
        this.props.connectDragPreview(getEmptyImage());
    }

    // @ts-expect-error
    componentDidUpdate(prevProps) {
        if (
            prevProps.width !== this.props.width ||
            prevProps.name !== this.props.name ||
            prevProps.isSorted !== this.props.isSorted
        ) {
            this.updateMaxTitleWidth();
        }
    }

    render() {
        const {
            index,
            field: {
                name,
                title,
                shortTitle,
                subtitle,
                type,
                isSortable,
                isFilterable: isSchemaFilterable,
            },
            filter,
            onSelectAll,
            isSorted,
            sortDirection,
            isDragging,
            connectDragSource,
            connectDropTarget,
            isOver,
            draggingIndex,
        } = this.props;

        const isOverToLeft = isOver && draggingIndex && draggingIndex > index;
        const isOverToRight = isOver && draggingIndex && draggingIndex < index;

        const isFilterable =
            // @ts-expect-error
            isSchemaFilterable && this.props.onUpdate && this.props.onRemove && this.props.onSubmit;

        const classes = this.getClasses('ui-entity-list__column-header', {
            'ui-entity-list__column-header--filtered': filter || this.state.popoverOpen,
            'ui-entity-list__column-header--filterable': isFilterable,
            'ui-entity-list__column-header--name': onSelectAll,
            'ui-entity-list__column-header--sortable': isSortable,
            'ui-entity-list__column-header--dragging': isDragging,
        });

        const value = filter
            ? // @ts-expect-error
              deserializeUrlParam(filter[Object.keys(filter)[0]], type)
            : undefined;

        const columnHeaderTitle = shortTitle ? shortTitle : title;

        let sortClasses = 'ui-entity-list__column-sort ui-btn--icon ui-column-header__ui-btn';
        if (isSorted && sortDirection) {
            sortClasses = sortClasses.concat(
                ` ui-entity-list__column-sort--${sortDirection} ui-entity-list__column-sort--active`
            );
        }

        const header: React.ReactElement<any> | null | undefined = connectDragSource(
            <div
                ref={(node) => {
                    this.columnWrapperRef = node;
                }}
                onClick={() => this.handleSort(name)}
                className={classnames(classes, styles['select-all'])}
            >
                {onSelectAll ? (
                    <div
                        ref={(node) => {
                            this.selectAllDivRef = node;
                        }}
                    >
                        <Checkbox
                            disabled={this.props.isDuplicatesMode}
                            checked={this.props.allSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onSelectAll()}
                        />
                    </div>
                ) : undefined}
                {isFilterable ? (
                    <button
                        ref={(node) => {
                            this.filterRef = node;
                        }}
                        onClick={(e) => this.handleFilterClick(e)}
                        className={classnames(
                            'ui-btn--icon ui-column-header__ui-btn ui-column-header__filter',
                            styles['column-button']
                        )}
                    >
                        <FilterIcon wrapWithDiv={false} size={12} />
                    </button>
                ) : undefined}
                <div
                    className='ui-column-header__column-title'
                    title={`${title}${subtitle ? ` - ${subtitle}` : ''}`}
                    style={{width: this.state.maxTitleWidth}}
                >
                    {columnHeaderTitle}
                    {subtitle ? (
                        <span className='ui-column-header__column-subtitle'>{subtitle}</span>
                    ) : undefined}
                </div>
                {isSortable ? (
                    <button
                        ref={(node) => {
                            this.sortRef = node;
                        }}
                        className={classnames(sortClasses, styles['column-button'])}
                    >
                        <ArrowsDescIcon wrapWithDiv={false} size={12} />
                        <ArrowsAscIcon wrapWithDiv={false} size={12} />
                    </button>
                ) : undefined}
                {isOverToLeft ? (
                    <div className='ui-column-header__drop-target-indicator ui-column-header__drop-target-indicator--left' />
                ) : undefined}
                {isOverToRight ? (
                    <div className='ui-column-header__drop-target-indicator ui-column-header__drop-target-indicator--right' />
                ) : undefined}
            </div>
        );

        if (header) {
            return (
                <React.Fragment>
                    {connectDropTarget(header)}
                    {this.state.popoverOpen ? (
                        <PopoverForm
                            anchor={this.filterRef}
                            // @ts-expect-error
                            className={`ui-popover-form__header-filter ui-popover-form__header-filter--${type}`}
                            filter={filter}
                            field={this.props.field}
                            value={value || undefined}
                            onClose={() => {
                                this.props.onPopoverClose();
                                this.setState({popoverOpen: false});
                            }}
                            onSubmit={(data) => this.handleSubmit(data, type, filter)}
                            onRemove={() => this.handleRemove(filter)}
                        />
                    ) : null}
                </React.Fragment>
            );
        }

        // Not certain under what circumstances header will be null,
        // but Flow says that connectDragSource can return null.
        // TODO (@ianvs 2017-06-12): Handle this better, maybe empty div?
        return null;
    }

    // @ts-expect-error
    handleSort(name) {
        if (!this.props.field.isSortable) return;

        this.props.onSort(name);
    }

    updateMaxTitleWidth() {
        if (!this.columnWrapperRef) return;

        // @ts-expect-error
        let titleWidth = $(this.columnWrapperRef).width() - 6; // Right border 1px + padding

        if (this.selectAllDivRef) {
            titleWidth -= this.selectAllDivRef.offsetWidth;
        }
        if (this.sortRef) {
            titleWidth -= this.sortRef.offsetWidth;
        }
        if (this.filterRef) {
            titleWidth -= this.filterRef.offsetWidth;
        }

        this.setState({maxTitleWidth: titleWidth});
    }

    // @ts-expect-error
    handleFilterClick(e) {
        e.stopPropagation();

        if (!this.state.popoverOpen) {
            this.props.onPopoverOpen();
            this.setState({popoverOpen: true});
        }
    }

    // @ts-expect-error
    handleSubmit(data, type, filter) {
        this.props.onPopoverClose();
        this.setState({popoverOpen: false});

        const newFilter = serialize(data, type);
        if (filter) {
            this.props.onUpdate?.(filter, newFilter);
        } else {
            this.props.onSubmit?.(newFilter);
        }
    }

    // @ts-expect-error
    handleRemove(filter) {
        this.props.onPopoverClose();
        this.setState({popoverOpen: false});
        this.props.onRemove?.(filter);
    }
}

export const ColumnHeader = DragSource(
    dragItemTypes.LIST_COLUMN,
    columnSource,
    collect
)(DropTarget(dragItemTypes.LIST_COLUMN, cardTarget, connectDrop)(ColumnHeaderComponent));
